import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { cn } from '../../lib/utils';
import { useLocation } from 'react-router-dom';

interface MainLayoutProps {
  children: ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mainShellRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isPublicFullscreenRoute =
    location.pathname === '/welcome' ||
    location.pathname === '/auth/callback' ||
    location.pathname === '/auth/activate-invite' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password';

  // Responsive navigation behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
        setMobileOpen(false);
      } else {
        setCollapsed(false);
      }
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Stabilize viewport height on mobile browsers (iOS/Android) where 100vh can be wrong
  useEffect(() => {
    const setViewportVars = () => {
      const visualViewport = window.visualViewport;
      const viewportHeight = visualViewport?.height ?? window.innerHeight;
      const viewportOffsetTop = visualViewport?.offsetTop ?? 0;
      const vh = viewportHeight * 0.01;
      const keyboardInset = Math.max(0, window.innerHeight - (viewportHeight + viewportOffsetTop));

      document.documentElement.style.setProperty('--app-vh', `${vh}px`);
      document.documentElement.style.setProperty('--app-viewport-height', `${viewportHeight}px`);
      document.documentElement.style.setProperty('--app-viewport-offset-top', `${viewportOffsetTop}px`);
      document.documentElement.style.setProperty('--app-keyboard-inset', `${keyboardInset}px`);
    };

    setViewportVars();

    window.addEventListener('resize', setViewportVars);
    window.addEventListener('orientationchange', setViewportVars);
    window.addEventListener('pageshow', setViewportVars);
    window.visualViewport?.addEventListener('resize', setViewportVars);
    window.visualViewport?.addEventListener('scroll', setViewportVars);

    return () => {
      window.removeEventListener('resize', setViewportVars);
      window.removeEventListener('orientationchange', setViewportVars);
      window.removeEventListener('pageshow', setViewportVars);
      window.visualViewport?.removeEventListener('resize', setViewportVars);
      window.visualViewport?.removeEventListener('scroll', setViewportVars);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  const blockBackgroundInteraction = isMobile && mobileOpen;

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const prevRootOverflow = root.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyOverscroll = body.style.overscrollBehavior;

    if (blockBackgroundInteraction) {
      root.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.style.overscrollBehavior = 'none';
    } else {
      root.style.overflow = '';
      body.style.overflow = '';
      body.style.overscrollBehavior = '';
    }

    return () => {
      root.style.overflow = prevRootOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.overscrollBehavior = prevBodyOverscroll;
    };
  }, [blockBackgroundInteraction]);

  useEffect(() => {
    if (!mainShellRef.current) return;
    const el = mainShellRef.current;

    if (blockBackgroundInteraction) {
      el.setAttribute('inert', '');
    } else {
      el.removeAttribute('inert');
    }
  }, [blockBackgroundInteraction]);

  if (isPublicFullscreenRoute) {
    return <>{children}</>;
  }

  return (
    <div
      className="flex overflow-hidden bg-background"
      style={{ height: 'var(--app-viewport-height, 100vh)' }}
    >
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {isMobile && mobileOpen && (
        <button
          className="fixed inset-0 bg-black/40 lg:hidden"
          style={{ zIndex: 'var(--z-mobile-backdrop)' }}
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation menu"
          data-testid="mobile-nav-backdrop"
        />
      )}
      
      <div 
        ref={mainShellRef}
        data-testid="app-main-shell"
        aria-hidden={blockBackgroundInteraction}
        className={cn(
          "flex flex-col flex-1 h-full transition-all duration-300 ease-in-out",
          blockBackgroundInteraction && "pointer-events-none select-none",
          isMobile ? "ml-0" : collapsed ? "ml-[80px]" : "ml-[280px]"
        )}
      >
        <Header
          onMenuClick={() => setMobileOpen(true)}
          isInteractionBlocked={blockBackgroundInteraction}
        />
        
        <main className="flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};
